/**
 * The typed contract between extracted React features and the
 * not-yet-extracted legacy runtime (app/app.js + app/suite.js).
 *
 * Top-level `function` declarations in classic scripts are writable window
 * bindings, and the legacy code resolves its own internal calls through
 * those bindings. That gives the migration its two seams:
 *
 *  - React CALLS legacy behaviour through these globals (routing onward,
 *    language state, i18n, auth plumbing) instead of duplicating it;
 *  - React REPLACES a legacy screen by overwriting its global, so every
 *    legacy caller transparently invokes the React implementation.
 *
 * Legacy top-level `const`/`let` live in the global lexical environment,
 * NOT on window — anything a React feature needs from those (art strings,
 * language tables) is ported verbatim and pinned by contract tests.
 *
 * legacyBridgeContract.test.ts pins every declaration listed here against
 * the oracle source so upstream drift is caught immediately.
 */

import type { PageVerse } from '../shared/quran/pageStructure';

/** The widget handle renderTurnstile returns. */
export interface TurnstileWidgetHandle {
  getToken(): string | null;
  reset(): void;
}

/** Context stashed across a sign-in redirect (see StoredPendingAuth). */
export interface PendingAuthContext {
  from: string;
  page?: number;
  gave?: boolean;
  mailingList?: boolean;
}

declare global {
  interface Window {
    /** false in environments whose Supabase has captcha off (staging); see environments/*.json */
    CAPTCHA_REQUIRED?: boolean;
    /* ---- screens React has taken over (writable seams) ---- */
    screenLanding?: () => void;
    screenLogin?: () => void;
    /** SCREEN 3 — set intention. The tour passes {onDone} to chain its steps. */
    screenIntention?: (opts?: { onDone?: () => void }) => void;

    /* ---- legacy behaviour React calls through the bridge ---- */
    /** Routes onward from the landing screen (o.from === 'landing' → intention). */
    route?: (o?: { from?: string }) => void;
    /** The "I'm new here" guided tour (legacy feature until stage 3). */
    startGuidedTour?: () => void;
    /** Signs out and repaints the landing screen. */
    doLogout?: () => void;
    /** UI translation lookup — reads the legacy I18N table and current language. */
    t?: (key: string, vars?: Record<string, string | number>) => string;
    /**
     * Applies a language change: mutates the legacy runtime's own state,
     * persists it, fetches the Qurʼan translation, sets document lang/dir,
     * then calls rerender. The one path that keeps legacy and React in sync.
     */
    setLanguage?: (code: string, rerender?: () => void) => Promise<void>;
    /** Renders a Turnstile widget into a container (explicit-render mode). */
    renderTurnstile?: (container: HTMLElement) => TurnstileWidgetHandle;
    /** Full-page redirect to Supabase's Google flow (stashes pending context itself). */
    sbGoogleSignIn?: (pending: PendingAuthContext) => void;
    /** Sends a magic sign-in link; throws with a message on failure. */
    sbMagicLink?: (email: string, captchaToken: string | null) => Promise<boolean>;
    /** Stashes reading context across an auth redirect. */
    rememberPendingAuth?: (context: PendingAuthContext | null) => void;
    /** The design suite's eased counter animation. */
    countUp?: (element: HTMLElement, target: number, durationMs: number) => void;
    /** Starts reserving the next page while the intention is being read. */
    prefetchNextPage?: () => Promise<unknown> | void;
    /**
     * Assigns the next page and opens it: disables/restores the given button,
     * shows the searching veil on a long wait, then calls `open`.
     */
    goToNextPage?: (button: HTMLElement | null, open?: () => void) => Promise<void>;
    /** After an intention: reading-preferences onboarding first time, else the reader. */
    routeAfterIntention?: () => Promise<void>;

    /* ---- SCREEN 4, the verse reader: the seam React takes over, and the legacy
            behaviour the React reader still calls through ---- */
    /** Opens the reader on the legacy `curPage`; full-page mode delegates to openMushaf. */
    openReader?: (opts?: LegacyReaderOpenOptions) => void;
    /** The printed-page reader (still legacy). */
    openMushaf?: (opts?: LegacyReaderOpenOptions) => void;
    /** Loads the IndoPak / Turkish word data the current script or layout needs. */
    needScript?: () => Promise<void>;
    /** Loads and registers the per-page tajweed font `v4p{page}`. */
    loadV4Font?: (page: number) => Promise<void>;
    pageVerses?: (page: number) => PageVerse[];
    glyphsOf?: (page: number) => Record<string, string>;
    juzOf?: (page: number) => number;
    surahTitleOf?: (verseKey: string) => string;
    /** Fills the legacy `byId[key]` row model and `window.WORDS[key]` for one laid-out verse. */
    registerVerse?: (verse: PageVerse, page: number, glyphs: Record<string, string>) => void;
    /** Writes the reading state (`qc2_*`) to localStorage. */
    persist?: () => void;
    /** Pushes the reading preferences to the signed-in account, debounced. */
    queueSettingsSync?: () => void;
    normalizeReadingUi?: <T extends { script: string; layout: string }>(state: T) => T;
    submitPagesRead?: (pages: number[]) => Promise<unknown>;
    /** The pages read this sitting are done: records the day and moves on to the donation flow. */
    flowFinishedReading?: (pages: number[]) => void;
    /** The nine-step reading-preferences onboarding. */
    startOnboarding?: () => void;
    /** Turns the bottom bar into the recitation player (reads `bar._onVerse`); the printed-page reader still uses it. */
    audioStart?: (bar: HTMLElement) => void;
    /** Fetches Quran.com's reciter list into REAL_AUDIO.reciters once and settles the default reciter. */
    loadReciters?: () => Promise<void>;
    /** The verse's audio file for the current reciter, cached; '' when Quran.com has none. */
    verseAudioUrl?: (verseKey: string) => Promise<string>;
    /** Set only in the share build: recitation streams are blocked there. */
    __NOAUDIO?: boolean;
    /** Plays one word's recitation and marks its chip `.playing`. */
    playWordAudio?: (loc: string, token: HTMLElement) => Promise<void>;
    /** The riffling-pages transition; resolves immediately under reduced motion. */
    flipPages?: (screen: HTMLElement, axis?: 'x' | 'y') => Promise<void>;
    /** During the guided tour, explains the finish box once the page is read. */
    tourPageComplete?: (handle: LegacyReaderHandle) => void;
    settingsSheet?: (state: LegacySettingsState) => LegacySettingsSheet;
    searchOverlay?: (screen: HTMLElement, opts: { onGo?: unknown }) => void;
    numTranslit?: (n: number) => string;
    surahNameGlyph?: (surah: number) => string;
    /** Per-verse word chips for the word-by-word layout, rebuilt on every render (legacy verseRow reads it). */
    WORDS?: Record<string, LegacyWordToken[]>;
    /** Authentic QPC text keyed by verse; the basmala row uses '1:1'. */
    QPC_TEXT?: Record<string, string>;

    /* ---- values legacy scripts publish on window ---- */
    /** Charity partner logo markup (charity-logo.js). */
    KLOGO?: string;
    /** The rising Qurʼan artwork used on the landing sky (charity-logo.js). */
    KQURANVEC?: string;
  }
}

/** A legacy global that must exist by the time an extracted screen uses it. */
export function requireLegacy<K extends keyof Window>(name: K): NonNullable<Window[K]> {
  const value = window[name];
  if (!value) throw new Error(`Legacy runtime global missing: ${String(name)}`);
  return value;
}

/**
 * Reads a top-level `let`/`const` binding from the legacy classic scripts.
 * Those live in the global lexical environment — not on window — but a
 * Function-constructor body evaluates in global scope and reaches them,
 * returning the LIVE value (objects stay shared, so mutations propagate
 * into the legacy runtime). Verified against the running app; the bridge
 * contract test pins each declaration read this way.
 */
export function readLegacyBinding<T>(name: string): T {
  return new Function('return ' + name)() as T;
}

/**
 * Assigns a top-level `let` binding of the legacy classic scripts (e.g. the
 * primitive `curPage`, which cannot be mutated through a shared reference).
 * Only names from the pinned contract are ever passed here.
 */
export function writeLegacyBinding<T>(name: string, value: T): void {
  new Function('value', name + ' = value')(value);
}

/** What the guided tour and onReady callers receive from the reader (legacy `S`). */
export interface LegacyReaderHandle {
  screen: HTMLElement;
  scroll: HTMLElement;
  content: HTMLElement;
  counter: HTMLElement;
  bar: HTMLElement;
  med: HTMLElement | null;
  settingsBtn: HTMLElement;
  undochip: HTMLElement | null;
  _appendPage?: () => Promise<void>;
}

export interface LegacyReaderOpenOptions {
  onReady?: (handle: LegacyReaderHandle) => void;
}

/** One word chip of the word-by-word layout (legacy registerVerse output). */
export interface LegacyWordToken {
  a: string;
  t?: string;
  m?: string;
  y?: 'end';
  loc?: string;
}

/** The legacy `byId[key]` row model registerVerse fills. */
export interface LegacyVerseModel {
  id: string;
  n: number;
  ar: string;
  tr: string;
  en: string;
  page: number;
  juz: number;
  surahName: string;
}

export interface LegacySettingsState {
  mode: 'vbv' | 'full' | 'arabic';
  layout: 'side' | 'stack' | 'words';
  translit: boolean;
  arSize: 'md' | 'lg';
  script: string;
}

/** The option nodes the design suite's settings sheet returns. */
export interface LegacySettingsSheet {
  dim: HTMLElement;
  sheet: HTMLElement;
  side: HTMLElement;
  words: HTMLElement;
  stack: HTMLElement;
  full: HTMLElement;
  arabicOnly: HTMLElement;
  sw: HTMLElement;
  tajwidSw: HTMLElement;
  scU: HTMLElement;
  scI: HTMLElement;
  scT: HTMLElement;
  arSlider: HTMLInputElement;
  enSlider: HTMLInputElement;
  done: HTMLElement;
}

/** The legacy runtime's live journey-flow state (`let flow` in app/app.js). */
export interface LegacyJourneyFlowBinding {
  intro: number;
  account: string | null;
  intention: string;
  gave?: string;
  hideDonateUntil?: string;
  mailingList?: boolean;
}

/**
 * Marks today's intention in the legacy runtime's own state and persists it,
 * exactly as the legacy begin-reading handler does — the in-memory copy must
 * change too, or every later legacy screen would see a stale intention.
 */
export function recordIntentionMadeToday(): void {
  const flow = readLegacyBinding<LegacyJourneyFlowBinding>('flow');
  flow.intention = readLegacyBinding<() => string>('todayKey')();
  readLegacyBinding<() => void>('saveFlow')();
}
